import { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { SessionUser } from '@tinyrewards/shared';
import { apiGet, apiPost, setSessionActive } from '../api/client';
import { pushAuthToExtension } from '../lib/extension-bridge';

interface AuthContextValue {
    user: SessionUser | null;
    initialEmailFailed: boolean;
    loading: boolean;
    login: (email: string, password: string) => Promise<{ success: boolean; message?: string }>;
    logout: () => Promise<void>;
    refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<SessionUser | null>(null);
    const [initialEmailFailed, setInitialEmailFailed] = useState(false);
    const [loading, setLoading] = useState(true);

    const refresh = async () => {
        try {
            const res = await apiGet<SessionUser>('/auth/me');
            const sessionUser = res.success ? (res.data ?? null) : null;
            setUser(sessionUser);
            // Tells the api layer whether a later 401 is an expired session
            // (redirect the user out) or just an anonymous visitor (leave alone).
            setSessionActive(sessionUser !== null);
            setInitialEmailFailed(
                res.success && (res as { initialEmailFailed?: boolean }).initialEmailFailed === true,
            );
            if (sessionUser) void pushAuthToExtension();
        } catch {
            setUser(null);
            setInitialEmailFailed(false);
            setSessionActive(false);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        refresh();
    }, []);

    const login = async (email: string, password: string) => {
        const res = await apiPost<SessionUser>('/auth/login', { email, password });
        if (res.success && res.data) {
            setUser(res.data);
            setSessionActive(true);
            void pushAuthToExtension();
        }
        return { success: res.success, message: res.message };
    };

    const logout = async () => {
        // Before the call, not after: a deliberate logout must not be mistaken
        // for an expired session if anything in flight 401s on the way out.
        setSessionActive(false);
        await apiPost('/auth/logout', {});
        setUser(null);
    };

    return (
        <AuthContext.Provider value={{ user, initialEmailFailed, loading, login, logout, refresh }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth(): AuthContextValue {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used within AuthProvider');
    return ctx;
}
